import eventBus from "@sd-utility/eventbus";
import { PageType, parsePLPURL } from "@tvg/product-listing-url";
import { getSEOMetadata } from "client/api/amplience";
import { RESULTS_UPDATE_EVENT } from "client/utils/consts";
import { getSEOMetadataDeliveryKey } from "common/utils/amplience";
import { generatePageTitle } from "common/utils/seo/seo-title";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { AmpSEOMetadata } from "types/amplience";
import { SearchAPIData } from "types/search-api";
import useGlobalConfig from "./GlobalConfig";

export interface SEOConfig {
  overrides?: AmpSEOMetadata;
  fallback?: SearchAPIData | null;
  unfilteredFacet?: string | undefined;
}

const SEOConfigContext = createContext<SEOConfig>({} as SEOConfig);
SEOConfigContext.displayName = "SEOConfigContext";

const handleCanoncialUpdate = (eventData: { path: string }) => {
  const carnoncialEl = document.querySelector('link[rel="canonical"]');
  const locationOrigin = window.location.origin;
  const newPath = eventData?.path;

  if (!newPath || !carnoncialEl) return;

  carnoncialEl.setAttribute("href", `${locationOrigin}${newPath}`);
};

export const SEOConfigProvider: React.FC<{
  value: SEOConfig;
  initPath: string;
  unfilteredFacet?: string | undefined;
  children?: React.ReactNode;
}> = ({ value: initValue, initPath, unfilteredFacet, children, ...props }) => {
  const [value, setValue] = useState({ ...initValue, unfilteredFacet });
  const pathRef = useRef(initPath);
  const { brand, pageType } = useGlobalConfig();

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    const subscription = eventBus.subscribe(RESULTS_UPDATE_EVENT, (e) => {
      clearTimeout(timeout);

      // Adding timeout to deal with double render causing pathRef to be overriden during process
      timeout = setTimeout(async () => {
        const url = new URL(e.path, location.origin);

        handleCanoncialUpdate(e);

        if (url.pathname === pathRef.current) return;

        pathRef.current = url.pathname;
        const deliveryKey = getSEOMetadataDeliveryKey(e.brand, e.path);
        const res = await getSEOMetadata(deliveryKey);
        setValue({ overrides: res, fallback: e.response, unfilteredFacet });

        if (pageType === PageType.Search) return;

        const newTitle =
          res?.title ||
          generatePageTitle({
            overrides: res,
            fallback: e.response,
            brand,
            plpurl: parsePLPURL(url.toString(), brand),
            pageType,
          });

        if (newTitle && document.title !== newTitle) {
          document.title = newTitle;
        }
      }, 0);
    });

    return () => {
      clearTimeout(timeout); // Cleanup any remaining timer
      subscription.unsubscribe(); // Unsubscribe on unmount or brand change
    };
  }, [brand]);

  return (
    <SEOConfigContext.Provider {...props} value={value}>
      {children}
    </SEOConfigContext.Provider>
  );
};

const useSEOConfig = () => useContext(SEOConfigContext);
export default useSEOConfig;
